import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Building2, Pencil, Plus, DoorOpen, Zap, Archive,
  MapPin, Users, Trash2,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, humanise, date } from '@/lib/format';
import {
  Badge, Button, Card, CardHeader, ConfirmDialog, DetailRow, EmptyState, Field, Input,
  Modal, MoneyInput, PageHeader, Select, Spinner, Tabs, Textarea,
  useToast, statusTone, Stat,
} from '@/components/ui';
import { Breadcrumb } from '@/components/Shell';
import { FileUploader, PhotoGrid } from '@/components/Files';
import { PropertyFormModal } from './Properties';
import { RoomFormModal } from './Rooms';
import type { Property, Room, Utility, StoredFile, Bill, Lease, UtilityBilling } from '@shared/types';

interface PropertyFull extends Property {
  rooms: (Room & { tenant_name: string | null; lease_id: string | null })[];
  utilities: Utility[];
  photos: StoredFile[];
  bills: Bill[];
  leases: Lease[];
}

const BILLING_MODES: { value: UtilityBilling; label: string; hint: string }[] = [
  { value: 'included_in_rent', label: 'Included in rent', hint: 'Never billed separately' },
  { value: 'split_equally', label: 'Split equally', hint: 'Divided across live leases' },
  { value: 'per_room_meter', label: 'Per room meter', hint: 'Each room metered separately' },
  { value: 'billed_to_landlord', label: 'Landlord pays', hint: 'Never recharged to tenants' },
  { value: 'prepaid_by_tenant', label: 'Tenant prepays', hint: 'Tenant buys their own' },
];

export default function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const { currency, can } = useAuth();
  const toast = useToast();

  const [tab, setTab] = useState('rooms');
  const [editing, setEditing] = useState(false);
  const [addingRoom, setAddingRoom] = useState(false);
  const [addingUtility, setAddingUtility] = useState<Utility | 'new' | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data, loading, reload } = useApi<PropertyFull>(id ? `/properties/${id}` : null);

  if (loading) return <div className="flex justify-center py-20"><Spinner className="h-8 w-8" /></div>;
  if (!data) {
    return (
      <EmptyState
        icon={<Building2 className="h-6 w-6" />}
        title="Property not found"
        action={<Link to="/app/properties"><Button>Back to properties</Button></Link>}
      />
    );
  }

  const perRoom = data.letting_mode === 'per_room';
  const occupied = data.rooms.filter((r) => r.status === 'occupied').length;
  const potentialRent = perRoom
    ? data.rooms.reduce((sum, r) => sum + r.rent_cents, 0)
    : data.rent_cents;
  const actualRent = perRoom
    ? data.rooms.filter((r) => r.status === 'occupied').reduce((sum, r) => sum + r.rent_cents, 0)
    : (data.leases.length > 0 ? data.rent_cents : 0);

  async function toggleArchive() {
    setBusy(true);
    try {
      await api.post(`/properties/${id}/archive`, { archived: !data!.is_archived });
      toast.success(data!.is_archived ? 'Property restored' : 'Property archived');
      setArchiving(false);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not archive it');
    } finally {
      setBusy(false);
    }
  }

  async function uploadPhoto(payload: { filename: string; mime_type: string; data: string }) {
    await api.post('/files', { ...payload, kind: 'property_photo', property_id: id, tenant_visible: true });
    reload();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={<Breadcrumb items={[{ label: 'Properties', to: '/app/properties' }, { label: data.name }]} />}
        title={
          <span className="flex items-center gap-3 flex-wrap">
            {data.name}
            {data.is_archived && <Badge tone="neutral">Archived</Badge>}
          </span>
        }
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            <span>{humanise(data.type)}</span>
            <span className="text-faint">·</span>
            <span>{perRoom ? 'Let room by room' : 'Let as a whole property'}</span>
            {(data.address_line1 || data.city) && (
              <>
                <span className="text-faint">·</span>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {[data.address_line1, data.city, data.country].filter(Boolean).join(', ')}
                </span>
              </>
            )}
          </span>
        }
        action={can('staff') && (
          <>
            <Button icon={<Archive className="h-4 w-4" />} onClick={() => setArchiving(true)}>
              {data.is_archived ? 'Restore' : 'Archive'}
            </Button>
            <Button variant="primary" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          </>
        )}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Potential rent"
          value={money(potentialRent, currency)}
          hint="If everything is let"
        />
        <Stat
          label="Current rent"
          value={money(actualRent, currency)}
          hint={potentialRent > 0 ? `${Math.round((actualRent / potentialRent) * 100)}% of potential` : undefined}
          tone={actualRent >= potentialRent && potentialRent > 0 ? 'ok' : undefined}
        />
        {perRoom && (
          <Stat label="Rooms" value={`${occupied}/${data.rooms.length}`} hint={`${data.rooms.length - occupied} available`} />
        )}
        <Stat label="Active tenants" value={String(data.leases.length)} icon={<Users className="h-4 w-4" />} />
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          ...(perRoom ? [{ id: 'rooms', label: 'Rooms', count: data.rooms.length }] : []),
          { id: 'tenants', label: 'Tenants', count: data.leases.length },
          { id: 'utilities', label: 'Utilities', count: data.utilities.length },
          { id: 'photos', label: 'Photos', count: data.photos.length },
          { id: 'bills', label: 'Bills', count: data.bills.length },
          { id: 'details', label: 'Details' },
        ]}
      />

      {/* Rooms */}
      {tab === 'rooms' && perRoom && (
        <Card>
          <CardHeader
            title="Rooms"
            subtitle="Occupancy follows the leases — assign a tenant to fill a room"
            action={can('staff') && (
              <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setAddingRoom(true)}>
                Add room
              </Button>
            )}
          />
          <div className="p-5 sm:p-6 pt-4">
            {data.rooms.length === 0 ? (
              <EmptyState
                icon={<DoorOpen className="h-6 w-6" />}
                title="No rooms yet"
                message="Add the rooms in this property, each with its own rent."
                action={can('staff') && <Button variant="primary" size="sm" onClick={() => setAddingRoom(true)}>Add room</Button>}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {data.rooms.map((room) => (
                  <div key={room.id} className="p-4 rounded-xl border border-line bg-raised/40">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-bold tracking-tight">{room.name}</p>
                        <p className="text-xs text-faint mt-0.5">
                          {room.room_type ?? 'Room'}{room.floor ? ` · Floor ${room.floor}` : ''}
                        </p>
                      </div>
                      <Badge tone={statusTone(room.status)} dot>{humanise(room.status)}</Badge>
                    </div>

                    <div className="flex items-baseline justify-between gap-2 mt-4 pt-3 border-t border-line">
                      <span className="font-bold tnum">{money(room.rent_cents, currency)}</span>
                      {room.tenant_name ? (
                        <Link to={`/app/tenants/${room.tenant_id}`} className="text-xs link truncate">
                          {room.tenant_name}
                        </Link>
                      ) : (
                        <span className="text-xs text-faint">Vacant</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Tenants */}
      {tab === 'tenants' && (
        <Card>
          <CardHeader title="Current tenants" subtitle="Live leases on this property" />
          <div className="p-5 sm:p-6 pt-4">
            {data.leases.length === 0 ? (
              <EmptyState
                icon={<Users className="h-6 w-6" />}
                title="Nobody is living here yet"
                message="Assign a tenant from the tenants page to create a lease."
                action={<Link to="/app/tenants"><Button variant="primary" size="sm">Go to tenants</Button></Link>}
              />
            ) : (
              <div className="space-y-2">
                {data.leases.map((lease) => (
                  <Link
                    key={lease.id}
                    to={`/app/tenants/${lease.tenant_id}`}
                    className="flex items-center gap-3 p-3 rounded-xl border border-line bg-raised/40 row-hover"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{lease.tenant_name}</p>
                      <p className="text-xs text-faint">
                        {lease.room_name ?? 'Whole property'} · from {date(lease.start_date)}
                        {lease.end_date ? ` to ${date(lease.end_date)}` : ''}
                      </p>
                    </div>
                    <span className="font-bold tnum shrink-0">{money(lease.rent_cents, currency)}</span>
                    <Badge tone={statusTone(lease.status)}>{humanise(lease.status)}</Badge>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Utilities */}
      {tab === 'utilities' && (
        <Card>
          <CardHeader
            title="Services & utilities"
            subtitle="How each service is charged decides whether a bill can be split to tenants"
            action={can('staff') && (
              <Button size="sm" variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setAddingUtility('new')}>
                Add utility
              </Button>
            )}
          />
          <div className="p-5 sm:p-6 pt-4">
            {data.utilities.length === 0 ? (
              <EmptyState
                icon={<Zap className="h-6 w-6" />}
                title="No utilities set up"
                message="Add electricity, water, gas, wifi and any other service so bills can be tracked against them."
                action={can('staff') && <Button variant="primary" size="sm" onClick={() => setAddingUtility('new')}>Add utility</Button>}
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {data.utilities.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => can('staff') && setAddingUtility(u)}
                    className="text-left p-4 rounded-xl border border-line bg-raised/40 row-hover"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{u.name}</p>
                        <p className="text-xs text-faint mt-0.5 truncate">
                          {u.provider ?? 'No provider'}{u.account_number ? ` · ${u.account_number}` : ''}
                        </p>
                      </div>
                      {!u.is_active && <Badge tone="neutral">Inactive</Badge>}
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-line">
                      <Badge tone="info">
                        {BILLING_MODES.find((m) => m.value === u.billing_mode)?.label ?? humanise(u.billing_mode)}
                      </Badge>
                      {u.fixed_cents > 0 && (
                        <span className="text-sm tnum text-muted">{money(u.fixed_cents, currency)}/mo</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Photos */}
      {tab === 'photos' && (
        <Card>
          <CardHeader
            title="Photos"
            subtitle="Shown here and in the tenant portal"
          />
          <div className="p-5 sm:p-6 pt-4 space-y-5">
            {can('staff') && (
              <FileUploader onUpload={uploadPhoto} kind="property_photo" label="Add photos of this property" />
            )}
            <PhotoGrid files={data.photos} onChange={reload} readOnly={!can('staff')} />
          </div>
        </Card>
      )}

      {/* Bills */}
      {tab === 'bills' && (
        <Card>
          <CardHeader
            title="Recent bills"
            subtitle="Electricity, water, gas and other outgoings"
            action={<Link to="/app/bills"><Button size="sm">All bills</Button></Link>}
          />
          <div className="p-5 sm:p-6 pt-4">
            {data.bills.length === 0 ? (
              <EmptyState icon={<Zap className="h-6 w-6" />} title="No bills recorded" />
            ) : (
              <div className="space-y-2">
                {data.bills.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-line bg-raised/40">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{humanise(b.category)}</p>
                      <p className="text-xs text-faint">
                        {b.provider ?? '—'} · due {date(b.due_date)}
                      </p>
                    </div>
                    <span className="font-bold tnum shrink-0">{money(b.amount_cents, currency)}</span>
                    <Badge tone={statusTone(b.status)}>{humanise(b.status)}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Details */}
      {tab === 'details' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader title="Property details" />
            <div className="p-5 sm:p-6 pt-4">
              <dl>
                <DetailRow label="Type">{humanise(data.type)}</DetailRow>
                <DetailRow label="Letting mode">{perRoom ? 'Room by room' : 'Whole property'}</DetailRow>
                <DetailRow label="Code">{data.code ?? '—'}</DetailRow>
                <DetailRow label="Bedrooms">{data.bedrooms ?? '—'}</DetailRow>
                <DetailRow label="Bathrooms">{data.bathrooms ?? '—'}</DetailRow>
                <DetailRow label="Year built">{data.year_built ?? '—'}</DetailRow>
                {!perRoom && (
                  <>
                    <DetailRow label="Rent">{money(data.rent_cents, currency)}</DetailRow>
                    <DetailRow label="Deposit">{money(data.deposit_cents, currency)}</DetailRow>
                  </>
                )}
                <DetailRow label="Added">{date(data.created_at)}</DetailRow>
              </dl>
              {data.notes && (
                <div className="mt-4 pt-4 border-t border-line">
                  <p className="label mb-2">Notes</p>
                  <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">{data.notes}</p>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Facilities" />
            <div className="p-5 sm:p-6 pt-4">
              {data.facilities.length === 0 ? (
                <p className="text-sm text-faint">None recorded.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.facilities.map((f) => <Badge key={f} tone="brand">{f}</Badge>)}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {editing && (
        <PropertyFormModal
          property={data}
          onClose={() => setEditing(false)}
          onDone={() => { setEditing(false); reload(); }}
        />
      )}

      {addingRoom && id && (
        <RoomFormModal
          propertyId={id}
          onClose={() => setAddingRoom(false)}
          onDone={() => { setAddingRoom(false); reload(); }}
        />
      )}

      {addingUtility && id && (
        <UtilityModal
          propertyId={id}
          utility={addingUtility === 'new' ? undefined : addingUtility}
          onClose={() => setAddingUtility(null)}
          onDone={() => { setAddingUtility(null); reload(); }}
        />
      )}

      <ConfirmDialog
        open={archiving}
        onClose={() => setArchiving(false)}
        onConfirm={() => void toggleArchive()}
        loading={busy}
        tone={data.is_archived ? 'primary' : 'danger'}
        title={data.is_archived ? 'Restore this property?' : 'Archive this property?'}
        confirmLabel={data.is_archived ? 'Restore' : 'Archive'}
        message={data.is_archived
          ? 'It will appear in the main list again.'
          : 'It will be hidden from the main list. History and bills are kept, and you can restore it at any time. Active leases must be ended first.'}
      />
    </div>
  );
}

function UtilityModal({ propertyId, utility, onClose, onDone }: {
  propertyId: string; utility?: Utility; onClose: () => void; onDone: () => void;
}) {
  const { currency, can } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: utility?.name ?? '',
    category: utility?.category ?? 'electricity',
    provider: utility?.provider ?? '',
    account_number: utility?.account_number ?? '',
    billing_mode: utility?.billing_mode ?? ('split_equally' as UtilityBilling),
    is_active: utility?.is_active ?? true,
    notes: utility?.notes ?? '',
  });
  const [fixedCents, setFixedCents] = useState(utility?.fixed_cents ?? 0);

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  async function submit() {
    if (!form.name.trim()) { toast.error('Give the utility a name'); return; }
    setBusy(true);
    try {
      const payload = { ...form, fixed_cents: fixedCents };
      if (utility) await api.patch(`/utilities/${utility.id}`, payload);
      else await api.post(`/properties/${propertyId}/utilities`, payload);
      toast.success(utility ? 'Utility updated' : 'Utility added');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save it');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.del(`/utilities/${utility!.id}`);
      toast.success('Utility removed');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove it');
    } finally {
      setBusy(false);
    }
  }

  const mode = BILLING_MODES.find((m) => m.value === form.billing_mode);

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={utility ? 'Edit utility' : 'Add a utility'}
        subtitle="Electricity, water, gas, wifi, refuse, security…"
        footer={
          <>
            {utility && can('manager') && (
              <Button variant="ghost" onClick={() => setDeleting(true)} disabled={busy}
                icon={<Trash2 className="h-4 w-4" />}>
                Remove
              </Button>
            )}
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={() => void submit()} loading={busy}>Save</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" required>
              <Input value={form.name} onChange={set('name')} placeholder="Electricity" autoFocus />
            </Field>
            <Field label="Category">
              <Select value={form.category} onChange={set('category')}>
                {['electricity', 'water', 'gas', 'internet', 'refuse', 'security', 'cleaning', 'other']
                  .map((c) => <option key={c} value={c}>{humanise(c)}</option>)}
              </Select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Provider">
              <Input value={form.provider} onChange={set('provider')} placeholder="Kenya Power" />
            </Field>
            <Field label="Account number">
              <Input value={form.account_number} onChange={set('account_number')} placeholder="KP-884213" />
            </Field>
          </div>

          <Field label="How is it charged?" hint={mode?.hint}>
            <Select value={form.billing_mode} onChange={set('billing_mode')}>
              {BILLING_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Select>
          </Field>

          <Field label="Fixed monthly amount" hint="For flat-rate services like wifi. Leave at zero for metered ones.">
            <MoneyInput valueCents={fixedCents} onChangeCents={setFixedCents} currency={currency} />
          </Field>

          <Field label="Notes">
            <Textarea value={form.notes} onChange={set('notes')} />
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => void remove()}
        loading={busy}
        title="Remove this utility?"
        confirmLabel="Remove"
        message="Bills already recorded against it are kept, but will no longer be linked."
      />
    </>
  );
}
